import { useEffect, useState } from "react";
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";
import { api } from "@/lib/api";
import { n2 } from "@/lib/format";

// Team-points accuracy before vs after the PTS scoring calibration, from graded pre-tip snapshots.
export function CalibrationAccuracy() {
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  useEffect(() => {
    api.get("/points-calibration/analysis").then((r) => setData(r.data)).catch(() => {});
    api.get("/points-calibration/trend").then((r) => setTrend(r.data.weeks || [])).catch(() => {});
  }, []);
  if (!data) return null;
  const o = data.overall;
  const improved = o && o.mae_after < o.mae_before;

  return (
    <section className="aa-section" data-testid="calibration-accuracy">
      <h2>Team points · calibration impact</h2>
      <p className="aa-explainer">
        Projected team points vs actual on graded pre-tip snapshots — BEFORE calibration (raw model) vs AFTER (current −{data.global_pct}% default + per-team). Lower MAE and bias nearer 0 are better.
      </p>
      {!o ? <p className="aa-empty" data-testid="calibration-accuracy-empty">No graded games yet.</p> : (
        <>
          <div className="aa-cards">
            <div className="aa-card" data-testid="cal-mae-before">
              <span>MAE before</span><strong>{n2(o.mae_before)}</strong>
              <small>bias {o.bias_before > 0 ? "+" : ""}{n2(o.bias_before)}</small>
              <em className="aa-vs">raw model · n={o.n}</em>
            </div>
            <div className="aa-card" data-testid="cal-mae-after">
              <span>MAE after</span><strong>{n2(o.mae_after)}</strong>
              <small>bias {o.bias_after > 0 ? "+" : ""}{n2(o.bias_after)}</small>
              <em className={improved ? "positive" : "negative"}>{improved ? "▲" : "▼"} {((1 - o.mae_after / o.mae_before) * 100).toFixed(1)}% vs before</em>
            </div>
          </div>
          {trend.length > 0 && (
            <div className="aa-chart" data-testid="calibration-trend-chart" style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend} margin={{ left: -18, top: 8 }}>
                  <CartesianGrid stroke="#1a3554" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="week" tick={{ fill: "#8fb0d2", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#6b87aa", fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "#071323", border: "1px solid #168aff", borderRadius: 8 }} />
                  <Legend />
                  <ReferenceLine y={0} stroke="#5a7698" strokeDasharray="4 4" />
                  <Line type="monotone" dataKey="bias_before" name="Bias before" stroke="#f0953f" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="bias_after" name="Bias after" stroke="#5ff0c0" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="aa-table-scroll">
            <table className="aa-table" data-testid="cal-team-table">
              <thead><tr><th>Team</th><th className="r">n</th><th className="r">MAE before / after</th><th className="r">Bias before / after</th><th className="r">Calib %</th></tr></thead>
              <tbody>
                {data.teams.map((t) => (
                  <tr key={t.team_id}>
                    <td>{t.code}</td>
                    <td className="r aa-number">{t.n}</td>
                    <td className="r aa-number"><span className={t.mae_after <= t.mae_before ? "aa-good" : "aa-bad"}>{n2(t.mae_after)}</span><span className="aa-vs"> / {n2(t.mae_before)}</span></td>
                    <td className="r aa-number">{t.bias_before > 0 ? "+" : ""}{n2(t.bias_before)}<span className="aa-vs"> → </span>{t.bias_after > 0 ? "+" : ""}{n2(t.bias_after)}</td>
                    <td className="r aa-number">−{t.current_pct}%{t.has_suggestion && <span className="aa-good"> → −{t.suggested_pct}%</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="aa-explainer">Suggested % (green) appears for teams with ≥{data.min_games} graded games and a steady bias ≥ {data.min_bias} pts. Apply them from Settings → Βαθμονόμηση πόντων.</p>
        </>
      )}
    </section>
  );
}
